import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button, Card, Input, StatTile } from "@/design-system";
import { Icon } from "@/components/Icon";
import { useStudentNotes, useStudentReservations } from "@/api/queries";
import { formatLongDate, formatMonth, formatTime } from "@/lib/format";
import { zonedMonthKey } from "@/lib/timezone";
import { displayName } from "@/lib/user";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { StudentBillingPanel } from "@/components/StudentBillingPanel";
import { SHEET_OVERLAY_STYLE, SHEET_STYLE } from "@/components/sheetStyles";
import type { Lesson, Student, StudentNote } from "@/lib/types";

/**
 * StudentDetailSheet — the teacher's view of a single student (issue #223).
 *
 * Opens as a bottom sheet when the teacher taps a student card on the
 * roster (or a student in a lesson's attendee list). It gathers what the
 * teacher needs in one place:
 *
 *   - identity: name, email, join date
 *   - load: upcoming vs. all-time booking counts
 *   - the lessons they have reserved (upcoming), so a teacher can spot
 *     someone booking far more than everyone else
 *   - their notes (read-only; adding happens in the edit overlay)
 *
 * "Edit details" hands off to the existing `StudentEditOverlay`, which
 * owns name/email editing and the note composer. "Delete" arms an inline
 * confirmation and then removes the account entirely (issue #307).
 */

export function StudentDetailSheet({
  student,
  token,
  deleting,
  deleteError,
  blocking,
  blockError,
  onClose,
  onEdit,
  onBlock,
  onUnblock,
  onDelete,
}: {
  student: Student;
  token: string | null;
  deleting: boolean;
  deleteError: string | null;
  blocking: boolean;
  blockError: string | null;
  onClose: () => void;
  onEdit: () => void;
  onBlock: (reason: string) => void;
  onUnblock: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  // Two-step destructive action: the button arms an inline confirmation
  // rather than deleting on first click (issue #307).
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Blocking is a two-step action too (issue #310): the button reveals an
  // optional reason input before the block is applied.
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const reservationsQuery = useStudentReservations(token, student.id, "upcoming");
  const historyQuery = useStudentReservations(token, student.id, "past");
  const notesQuery = useStudentNotes(token, student.id);
  const lessons: Lesson[] = reservationsQuery.data ?? [];
  const history: Lesson[] = historyQuery.data ?? [];
  const notes: StudentNote[] = notesQuery.data ?? [];

  // Group past lessons into months (newest first — the API already orders
  // `scope=past` descending), so the teacher sees a month-by-month
  // breakdown of how much the student has booked.
  const historyByMonth = useMemo(() => {
    const groups: { key: string; label: string; lessons: Lesson[] }[] = [];
    const index = new Map<string, number>();

    for (const lesson of history) {
      const key = zonedMonthKey(new Date(lesson.starts_at));
      let at = index.get(key);

      if (at === undefined) {
        at = groups.length;
        index.set(key, at);
        groups.push({ key, label: formatMonth(lesson.starts_at), lessons: [] });
      }

      groups[at].lessons.push(lesson);
    }

    return groups;
  }, [history]);

  // Escape closes the sheet, matching the rest of the app's overlays.
  // While the delete confirmation is armed, the first Escape disarms it
  // instead of closing the sheet.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (confirmingDelete) setConfirmingDelete(false);
      else if (confirmingBlock) setConfirmingBlock(false);
      else onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, confirmingDelete, confirmingBlock]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("teacher.studentDetailHeading")}
      data-testid="student-detail-sheet"
      style={SHEET_OVERLAY_STYLE}
      onClick={onClose}
    >
      <Card
        padding="var(--space-5)"
        onClick={(e) => e.stopPropagation()}
        style={SHEET_STYLE}
      >
        {/* identity */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <Avatar name={displayName(student)} size={56} ring />
            <div style={{ minWidth: 0 }}>
              <div
                data-testid="student-detail-name"
                style={{
                  font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
                  color: "var(--text-strong)",
                  letterSpacing: "var(--ls-tight)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName(student)}
              </div>
              <div
                data-testid="student-detail-email"
                style={{
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                  color: "var(--text-muted)",
                  marginTop: 4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {student.email}
              </div>
              <div
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                  marginTop: 4,
                }}
              >
                {t("teacher.joined", { date: formatLongDate(student.joined_at) })}
              </div>
              {student.blocked && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Badge tone="danger" variant="solid" data-testid="student-detail-blocked">
                    {t("teacher.studentBlockedBadge")}
                  </Badge>
                  {student.block_reason ? (
                    <span
                      data-testid="student-detail-block-reason"
                      style={{
                        font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                        color: "var(--text-faint)",
                      }}
                    >
                      {student.block_reason}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="student-detail-close"
          >
            {t("common.close")}
          </Button>
        </div>

        {/* load */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: "var(--space-4)",
          }}
        >
          <StatTile
            tone="brand"
            value={student.upcoming_bookings}
            label={t("teacher.studentDetailUpcoming")}
            data-testid="student-detail-upcoming"
          />
          <StatTile
            tone="neutral"
            value={student.total_bookings}
            label={t("teacher.studentDetailTotal")}
            data-testid="student-detail-total"
          />
        </div>

        {/* billing — membership, credits and ledger (issue #248) */}
        <StudentBillingPanel student={student} token={token} />

        {/* reserved lessons */}
        <div
          style={{
            marginTop: "var(--space-5)",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "var(--space-4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                letterSpacing: "var(--ls-wide)",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              {t("teacher.studentDetailReservedHeading")}
            </span>
            <Badge tone="neutral" data-testid="student-detail-reserved-count">
              {reservationsQuery.isSuccess ? lessons.length : student.upcoming_bookings}
            </Badge>
          </div>

          {reservationsQuery.isLoading && (
            <div
              data-testid="student-detail-reserved-loading"
              style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
            >
              {t("teacher.studentDetailReservedLoading")}
            </div>
          )}

          {!reservationsQuery.isLoading && reservationsQuery.isError && (
            online ? (
              <div
                role="alert"
                data-testid="student-detail-reserved-error"
                style={{
                  background: "var(--status-danger-bg)",
                  color: "var(--status-danger-fg)",
                  padding: "var(--space-3) var(--space-4)",
                  borderRadius: "var(--radius-md)",
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                }}
              >
                {reservationsQuery.error?.message ??
                  t("teacher.studentDetailReservedError")}
              </div>
            ) : (
              <OfflineErrorState />
            )
          )}

          {!reservationsQuery.isLoading &&
            !reservationsQuery.isError &&
            lessons.length === 0 && (
              <div
                data-testid="student-detail-reserved-empty"
                style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
              >
                {t("teacher.studentDetailReservedEmpty")}
              </div>
            )}

          {!reservationsQuery.isLoading &&
            !reservationsQuery.isError &&
            lessons.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {lessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    data-testid={`student-detail-lesson-${lesson.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "var(--space-2) var(--space-3)",
                      background: "var(--surface-muted)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          font: "var(--fw-bold) var(--fs-2xs)/1 var(--font-mono)",
                          color: "var(--text-faint)",
                        }}
                      >
                        {formatLongDate(lesson.starts_at)} · {formatTime(lesson.starts_at)}
                      </div>
                      <div
                        style={{
                          font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                          color: "var(--text-strong)",
                          marginTop: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lesson.title}
                      </div>
                    </div>
                    <Badge
                      tone={lesson.lesson_type === "assigned" ? "info" : "neutral"}
                      style={{ flex: "none" }}
                    >
                      {lesson.lesson_type === "assigned"
                        ? t("teacher.typeAssigned")
                        : t("teacher.typeOpen")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* history — past lessons, grouped by month */}
        <div
          style={{
            marginTop: "var(--space-5)",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "var(--space-4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                letterSpacing: "var(--ls-wide)",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              {t("teacher.studentDetailHistoryHeading")}
            </span>
            <Badge tone="neutral" data-testid="student-detail-history-count">
              {historyQuery.isSuccess ? history.length : student.total_bookings}
            </Badge>
          </div>

          {historyQuery.isLoading && (
            <div
              data-testid="student-detail-history-loading"
              style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
            >
              {t("teacher.studentDetailHistoryLoading")}
            </div>
          )}

          {!historyQuery.isLoading && historyQuery.isError && (
            online ? (
              <div
                role="alert"
                data-testid="student-detail-history-error"
                style={{
                  background: "var(--status-danger-bg)",
                  color: "var(--status-danger-fg)",
                  padding: "var(--space-3) var(--space-4)",
                  borderRadius: "var(--radius-md)",
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                }}
              >
                {historyQuery.error?.message ?? t("teacher.studentDetailHistoryError")}
              </div>
            ) : (
              <OfflineErrorState />
            )
          )}

          {!historyQuery.isLoading && !historyQuery.isError && history.length === 0 && (
            <div
              data-testid="student-detail-history-empty"
              style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
            >
              {t("teacher.studentDetailHistoryEmpty")}
            </div>
          )}

          {!historyQuery.isLoading &&
            !historyQuery.isError &&
            historyByMonth.map((group) => (
              <div
                key={group.key}
                data-testid={`student-detail-history-${group.key}`}
                style={{ marginBottom: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      font: "var(--fw-bold) var(--fs-sm)/1.2 var(--font-display)",
                      color: "var(--text-strong)",
                    }}
                  >
                    {group.label}
                  </span>
                  <Badge tone="brand">
                    {t("teacher.studentDetailHistoryMonthCount", {
                      count: group.lessons.length,
                    })}
                  </Badge>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {group.lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      data-testid={`student-detail-lesson-${lesson.id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "var(--space-2) var(--space-3)",
                        background: "var(--surface-muted)",
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            font: "var(--fw-bold) var(--fs-2xs)/1 var(--font-mono)",
                            color: "var(--text-faint)",
                          }}
                        >
                          {formatLongDate(lesson.starts_at)} · {formatTime(lesson.starts_at)}
                        </div>
                        <div
                          style={{
                            font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                            color: "var(--text-strong)",
                            marginTop: 4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {lesson.title}
                        </div>
                      </div>
                      <Badge
                        tone={lesson.lesson_type === "assigned" ? "info" : "neutral"}
                        style={{ flex: "none" }}
                      >
                        {lesson.lesson_type === "assigned"
                          ? t("teacher.typeAssigned")
                          : t("teacher.typeOpen")}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {/* notes */}
        <div
          style={{
            marginTop: "var(--space-5)",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "var(--space-4)",
          }}
        >
          <div
            style={{
              font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
              letterSpacing: "var(--ls-wide)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
              marginBottom: 10,
            }}
          >
            {t("teacher.notesHeading")}
          </div>

          {notesQuery.isLoading && (
            <div style={{ color: "var(--text-faint)", fontSize: "var(--fs-sm)" }}>
              {t("common.loading")}
            </div>
          )}

          {!notesQuery.isLoading && notesQuery.isError && (
            <div
              role="alert"
              data-testid="student-detail-notes-error"
              style={{
                background: "var(--status-danger-bg)",
                color: "var(--status-danger-fg)",
                padding: "var(--space-3) var(--space-4)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              {notesQuery.error?.message ?? t("teacher.noteLoadError")}
            </div>
          )}

          {!notesQuery.isLoading && !notesQuery.isError && notes.length === 0 && (
            <div
              data-testid="student-detail-notes-empty"
              style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
            >
              {t("teacher.notesEmpty")}
            </div>
          )}

          {!notesQuery.isLoading &&
            !notesQuery.isError &&
            notes.map((note) => (
              <div
                key={note.id}
                data-testid={`student-detail-note-${note.id}`}
                style={{
                  background: "var(--surface-muted)",
                  borderRadius: "var(--radius-md)",
                  padding: "var(--space-3) var(--space-4)",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  {displayName(note.author)} · {formatLongDate(note.created_at)}
                </div>
                <div
                  style={{
                    font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
                    color: "var(--text-body)",
                    whiteSpace: "pre-wrap",
                    // A pasted URL or email in a note must wrap, not widen the sheet.
                    overflowWrap: "anywhere",
                  }}
                >
                  {note.body}
                </div>
              </div>
            ))}
        </div>

        {/* block / suspend (issue #310) */}
        <div style={{ marginTop: "var(--space-4)" }}>
          {student.blocked ? (
            <Button
              variant="secondary"
              onClick={onUnblock}
              disabled={blocking}
              style={{ width: "100%" }}
              data-testid="student-detail-unblock"
            >
              {blocking ? t("teacher.studentUnblocking") : t("teacher.studentUnblock")}
            </Button>
          ) : confirmingBlock ? (
            <div
              data-testid="student-detail-confirm-block-panel"
              style={{
                padding: "var(--space-3) var(--space-4)",
                background: "var(--surface-muted)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <div
                style={{
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                  color: "var(--text-strong)",
                }}
              >
                {t("teacher.studentBlockConfirm", { name: displayName(student) })}
              </div>
              <div style={{ marginTop: 10 }}>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  label={t("teacher.studentBlockReasonLabel")}
                  placeholder={t("teacher.studentBlockReasonPlaceholder")}
                  data-testid="student-detail-block-reason-input"
                />
              </div>
              <div
                style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setConfirmingBlock(false);
                    setBlockReason("");
                  }}
                  disabled={blocking}
                  data-testid="student-detail-cancel-block"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onBlock(blockReason.trim())}
                  disabled={blocking}
                  data-testid="student-detail-confirm-block"
                >
                  {blocking ? t("teacher.studentBlocking") : t("teacher.studentBlock")}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setConfirmingBlock(true)}
              disabled={blocking}
              style={{ width: "100%" }}
              data-testid="student-detail-block"
            >
              {t("teacher.studentBlock")}
            </Button>
          )}

          {blockError && (
            <div
              role="alert"
              data-testid="student-detail-block-error"
              style={{
                font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
                color: "var(--status-danger-fg)",
                marginTop: 6,
              }}
            >
              {blockError}
            </div>
          )}
        </div>

        <div style={{ marginTop: "var(--space-4)" }}>
          {confirmingDelete ? (
            <div
              data-testid="student-detail-confirm-delete-panel"
              style={{
                padding: "var(--space-3) var(--space-4)",
                background: "var(--status-danger-bg)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <div
                style={{
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                  color: "var(--status-danger-fg)",
                }}
              >
                {t("teacher.studentDeleteConfirm", { name: displayName(student) })}
              </div>
              {deleteError && (
                <div
                  role="alert"
                  data-testid="student-detail-delete-error"
                  style={{
                    font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
                    color: "var(--status-danger-fg)",
                    marginTop: 6,
                  }}
                >
                  {deleteError}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 10,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  data-testid="student-detail-cancel-delete"
                >
                  {t("teacher.keep")}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={onDelete}
                  disabled={deleting}
                  data-testid="student-detail-confirm-delete"
                >
                  {deleting ? t("teacher.studentDeleting") : t("teacher.studentDelete")}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="secondary"
                onClick={onEdit}
                iconLeft={<Icon name="settings" size={18} />}
                style={{ flex: 1 }}
                data-testid="student-detail-edit"
              >
                {t("teacher.studentDetailEdit")}
              </Button>
              <Button
                variant="danger"
                onClick={() => setConfirmingDelete(true)}
                iconLeft={<Icon name="trash" size={18} />}
                style={{ flex: 1 }}
                data-testid="student-detail-delete"
              >
                {t("teacher.studentDelete")}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
